/**
 * Toast Container Component
 *
 * Displays toast notifications with animations and styling.
 */

import React from 'react';
import { useToast, Toast, ToastType } from '../../contexts/ToastContext';

/**
 * Get icon for toast type
 */
function getToastIcon(type: ToastType): string {
  switch (type) {
    case 'info':
      return 'i';
    case 'success':
      return '\u2713'; // checkmark
    case 'warning':
      return '!';
    case 'error':
      return '\u2717'; // x mark
    default:
      return 'i';
  }
}

/**
 * Get background color for toast type
 */
function getToastColor(type: ToastType): string {
  switch (type) {
    case 'info':
      return '#3B82F6'; // blue
    case 'success':
      return '#22C55E'; // green
    case 'warning':
      return '#F59E0B'; // amber
    case 'error':
      return '#EF4444'; // red
    default:
      return '#3B82F6';
  }
}

/**
 * Single toast component
 */
interface ToastItemProps {
  toast: Toast;
  onClose: (id: string) => void;
}

function ToastItem({ toast, onClose }: ToastItemProps) {
  const color = getToastColor(toast.type);
  const icon = getToastIcon(toast.type);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        padding: '12px 16px',
        backgroundColor: '#1F2937',
        borderRadius: '8px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '8px',
        minWidth: '300px',
        maxWidth: '400px',
        animation: 'slideIn 0.3s ease-out',
        borderLeft: `4px solid ${color}`,
      }}
      role="alert"
    >
      {/* Icon */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: color,
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold',
          marginRight: '12px',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {toast.title && (
          <div
            style={{
              color: 'white',
              fontWeight: 600,
              fontSize: '14px',
              marginBottom: '4px',
            }}
          >
            {toast.title}
          </div>
        )}
        <div
          style={{
            color: '#D1D5DB',
            fontSize: '13px',
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {toast.message}
        </div>
      </div>

      {/* Close button */}
      <button
        onClick={() => onClose(toast.id)}
        style={{
          background: 'none',
          border: 'none',
          color: '#9CA3AF',
          cursor: 'pointer',
          padding: '4px',
          marginLeft: '8px',
          fontSize: '16px',
          lineHeight: 1,
          opacity: 0.7,
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
        aria-label="Close notification"
      >
        \u00d7
      </button>
    </div>
  );
}

/**
 * Toast container component
 */
export function ToastContainer() {
  const { toasts, removeToast } = useToast();

  if (toasts.length === 0) {
    return null;
  }

  return (
    <>
      {/* CSS Animation */}
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>

      {/* Toast container */}
      <div
        style={{
          position: 'fixed',
          top: '16px',
          right: '16px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
        role="region"
        aria-label="Notifications"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
        ))}
      </div>
    </>
  );
}

export default ToastContainer;
